import {Box} from '@primer/react'
import {graphql} from 'react-relay'
import {useFragment} from 'react-relay/hooks'

import {LABELS} from '../constants'
import type {CrossReferencedEvent$key} from './__generated__/CrossReferencedEvent.graphql'
import {TimelineRow} from './row/TimelineRow'
import {CrossReferencedEventInner} from './CrossReferencedEventInner'
import {LinkExternalIcon} from '@primer/octicons-react'

export type ReferenceTypes = 'issues' | 'prs' | 'mixed'

type CrossReferencedEventProps = {
  queryRef: CrossReferencedEvent$key
  rollupGroup?: Record<string, Array<CrossReferencedEvent$key & {source?: {__typename: string}}>>
  issueUrl: string
  highlightedEventId?: string
  refAttribute?: React.MutableRefObject<HTMLDivElement | null>
}

type sourceType = 'Issue' | 'PullRequest'

export const CrossReferencedEventFragment = graphql`
  fragment CrossReferencedEvent on CrossReferencedEvent {
    referencedAt
    willCloseTarget
    databaseId
    target {
      ... on Issue {
        repository {
          id
        }
      }
    }
    innerSource: source {
      __typename
      ...CrossReferencedEventInner
    }
    actor {
      ...EventActor
    }
  }
`

export function CrossReferencedEvent({
  queryRef,
  issueUrl,
  highlightedEventId,
  refAttribute,
  rollupGroup,
}: CrossReferencedEventProps): JSX.Element {
  const {actor, referencedAt, willCloseTarget, innerSource, databaseId, target} = useFragment(
    CrossReferencedEventFragment,
    queryRef,
  )

  const isIssue = innerSource.__typename === 'Issue'
  const isPullRequest = innerSource.__typename === 'PullRequest'

  if (!isIssue && !isPullRequest) {
    return <></>
  }

  const rolledUpGroup = rollupGroup && rollupGroup['CrossReferencedEvent'] ? rollupGroup['CrossReferencedEvent'] : []
  const hasMixedReferenceTypes = rolledUpGroup.some(item => item.source?.__typename !== innerSource.__typename)

  const message = buildMessage(innerSource.__typename, willCloseTarget, rolledUpGroup?.length, hasMixedReferenceTypes)

  const highlighted = String(databaseId) === highlightedEventId

  const itemsToRender = rolledUpGroup.length === 0 ? [queryRef] : rolledUpGroup

  return (
    <TimelineRow
      highlighted={highlighted}
      refAttribute={refAttribute}
      actor={actor}
      createdAt={referencedAt}
      showAgoTimestamp={false}
      deepLinkUrl={issueUrl}
      leadingIconOnTop={true}
      leadingIcon={LinkExternalIcon}
    >
      <TimelineRow.Main>{message}</TimelineRow.Main>
      <TimelineRow.Secondary>
        <Box as="section" aria-label={LABELS.crossReferencedEvent.sectionLabel}>
          <Box
            as="ul"
            sx={{
              mt: 2,
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            {itemsToRender.map((item, index) => (
              <CrossReferenceItem
                key={`${databaseId}_${index}`}
                event={item}
                targetRepositoryId={target.repository?.id}
              />
            ))}
          </Box>
        </Box>
      </TimelineRow.Secondary>
    </TimelineRow>
  )
}

function CrossReferenceItem({
  event,
  targetRepositoryId,
}: {
  event: CrossReferencedEvent$key
  targetRepositoryId: string | undefined
}) {
  const {innerSource} = useFragment(CrossReferencedEventFragment, event)
  return <CrossReferencedEventInner queryRef={innerSource} targetRepositoryId={targetRepositoryId} />
}

function buildMessage(
  sourceType: sourceType,
  willCloseTarget: boolean,
  rolledUpGroupLength: number,
  mixedReferenceTypes: boolean,
) {
  if (willCloseTarget) {
    return LABELS.timeline.linkedAClosingPR
  }
  if (rolledUpGroupLength === 0 || mixedReferenceTypes) {
    return LABELS.timeline.mentionedThisIn
  }
  if (sourceType === 'Issue') {
    return `${LABELS.timeline.mentionedThisIn} in ${rolledUpGroupLength} issues`
  } else {
    return `${LABELS.timeline.mentionedThisIn} in ${rolledUpGroupLength} pull requests`
  }
}

try{ CrossReferencedEvent.displayName ||= 'CrossReferencedEvent' } catch {}
try{ CrossReferenceItem.displayName ||= 'CrossReferenceItem' } catch {}